import { ComponentProps, useMemo } from 'react'
import {
    Brush,
    XAxis,
    ResponsiveContainer,
    ComposedChart,
    Line,
    ReferenceDot,
    YAxis,
} from 'recharts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
    IntervalReportSettings,
    IntervalHighlightPoint,
} from '@/hooks/useApi/useActiveManagementApi/types'
import { getStrokeHexValue } from '@/components/organisms/MultiChart/getStrokeHexValue'
import { useIntervalXAxisFormatter } from './useIntervalXAxisFormatter'

dayjs.extend(utc)

const HIGHLIGHT_DOT_R = 4

export type IntervalDataChartMiniMapProps = {
    data: (Record<string, unknown> & { date: string; timestamp: number })[]
    brushProps?: ComponentProps<typeof Brush>
    reportSettings: IntervalReportSettings
    highlightPoints?: IntervalHighlightPoint[]
    showHighlights?: boolean
}

export const IntervalDataChartMiniMap: React.FC<
    IntervalDataChartMiniMapProps
> = (props) => {
    const {
        data,
        brushProps,
        reportSettings,
        highlightPoints = [],
        showHighlights = false,
    } = props

    const { xAxisTickFormatter, xAxisFormat } = useIntervalXAxisFormatter(data)

    // Generate ticks within the actual data timestamp range
    // This ensures ticks are created only within dataMin and dataMax interval
    const filteredXAxisTicks = useMemo((): number[] | undefined => {
        if (data.length === 0) {
            return undefined
        }

        // Calculate actual min/max timestamps from the data
        let minTimestamp: number = 0
        let maxTimestamp: number = 0
        for (const entry of data) {
            const timestamp = entry.timestamp
            if (isNaN(Number(timestamp))) continue
            if (timestamp < minTimestamp) minTimestamp = timestamp
            if (timestamp > maxTimestamp) maxTimestamp = timestamp
        }

        const minDate = dayjs.utc(minTimestamp)
        const ticks: number[] = []

        if (xAxisFormat === 'year') {
            // Generate ticks at start of each year within the data range
            // Always include minTimestamp as first tick
            ticks.push(minTimestamp)

            // Find the next year start that's within range
            let current = minDate.startOf('year')
            if (current.valueOf() <= minTimestamp) {
                current = current.add(1, 'year')
            }

            // Add year starts until we exceed maxTimestamp
            while (current.valueOf() <= maxTimestamp) {
                ticks.push(current.valueOf())
                current = current.add(1, 'year')
            }

            // Always include maxTimestamp as last tick if it's not already included
            if (ticks[ticks.length - 1] !== maxTimestamp) {
                ticks.push(maxTimestamp)
            }
        } else if (xAxisFormat === 'month') {
            // Generate ticks at start of each month within the data range
            // Always include minTimestamp as first tick
            ticks.push(minTimestamp)

            // Find the next month start that's within range
            let current = minDate.startOf('month')
            if (current.valueOf() <= minTimestamp) {
                current = current.add(1, 'month')
            }

            // Add month starts until we exceed maxTimestamp
            while (current.valueOf() <= maxTimestamp) {
                ticks.push(current.valueOf())
                current = current.add(1, 'month')
            }

            // Always include maxTimestamp as last tick if it's not already included
            if (ticks[ticks.length - 1] !== maxTimestamp) {
                ticks.push(maxTimestamp)
            }
        } else {
            // Day format - use evenly spaced ticks similar to IntervalDataChart.tsx
            const span = maxTimestamp - minTimestamp
            if (span <= 0) {
                return [minTimestamp]
            }

            const TARGET_TICK_COUNT = 6
            const tickInterval = span / (TARGET_TICK_COUNT - 1)

            // Generate evenly spaced ticks within the range
            for (let i = 0; i < TARGET_TICK_COUNT; i++) {
                const tick = minTimestamp + i * tickInterval
                // Ensure tick is within bounds
                if (tick >= minTimestamp && tick <= maxTimestamp) {
                    ticks.push(tick)
                }
            }

            // Ensure we have at least minTimestamp and maxTimestamp
            if (ticks.length === 0) {
                ticks.push(minTimestamp, maxTimestamp)
            } else {
                // Ensure first tick is minTimestamp and last is maxTimestamp
                if (ticks[0] !== minTimestamp) {
                    ticks.unshift(minTimestamp)
                }
                if (ticks[ticks.length - 1] !== maxTimestamp) {
                    ticks.push(maxTimestamp)
                }
            }
        }

        // Ensure we have at least one tick
        if (ticks.length === 0) {
            return [minTimestamp]
        }

        // Sort and deduplicate ticks
        const sortedTicks = [...new Set(ticks)].sort((a, b) => a - b)
        return sortedTicks
    }, [data, xAxisFormat])

    // Use dataMin and dataMax for the minimap domain
    const xAxisDomain: [number | 'dataMin', number | 'dataMax'] = [
        'dataMin',
        'dataMax',
    ]

    // Extract line keys based on dataGrouping
    const lineKeys = useMemo(() => {
        if (data.length === 0) return []

        if (reportSettings.dataGrouping === 'aggregate') {
            // Aggregate mode: use "value" key
            return ['value']
        } else {
            // Compare mode: extract all meterId keys (numeric strings) and sort them
            const keys = Object.keys(data[0] || {})
            const meterIdKeys = keys.filter((key) => {
                // Filter for numeric keys only, excluding metadata fields
                return (
                    !isNaN(Number(key)) &&
                    key !== 'date' &&
                    key !== 'value' &&
                    key !== 'timestamp'
                )
            })
            // Sort numerically to ensure consistent color matching with main chart
            return meterIdKeys.sort((a, b) => Number(a) - Number(b))
        }
    }, [data, reportSettings.dataGrouping])

    return (
        <div className="relative mb-4 h-20 overflow-hidden">
            <div className="absolute bottom-0 left-0 right-0 top-0">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data}>
                        {lineKeys.map((key, index) => (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                stroke={getStrokeHexValue(index)}
                                strokeWidth={2}
                                dot={false}
                            />
                        ))}
                        {showHighlights &&
                            highlightPoints.length > 0 &&
                            highlightPoints.map((point, idx) => {
                                const colorIndex =
                                    reportSettings.dataGrouping === 'aggregate'
                                        ? 0
                                        : lineKeys.indexOf(
                                              String(point.meterId ?? '')
                                          )
                                const fill =
                                    colorIndex >= 0
                                        ? getStrokeHexValue(colorIndex)
                                        : getStrokeHexValue(0)
                                return (
                                    <ReferenceDot
                                        key={`highlight-${String(point.timestamp)}-${String(point.meterId ?? idx)}`}
                                        x={point.timestamp}
                                        y={point.value}
                                        r={HIGHLIGHT_DOT_R}
                                        fill={fill}
                                        stroke="white"
                                        strokeWidth={1}
                                    />
                                )
                            })}
                        <YAxis className="invisible" />
                        <XAxis
                            dataKey="timestamp"
                            axisLine={false}
                            tickLine={false}
                            dy={16}
                            tickFormatter={xAxisTickFormatter}
                            className="text-label-xsmall whitespace-nowrap text-subtle"
                            domain={xAxisDomain}
                            ticks={filteredXAxisTicks}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <div className="absolute bottom-0 left-0 right-0 top-0">
                {brushProps && (
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data}>
                            <YAxis className="invisible" />
                            <XAxis
                                dataKey="timestamp"
                                scale="time"
                                type="number"
                                domain={xAxisDomain}
                                hide
                            />
                            <Brush
                                {...(brushProps as Omit<
                                    ComponentProps<typeof Brush>,
                                    'ref'
                                >)}
                                fill="transparent"
                                stroke="#0081E6"
                                dx={0}
                                dy={0}
                                y={0}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    )
}
